import { useContext } from 'react';
import { atom, useAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { GroupId, GroupSharedState, GroupStateContext } from './types';

/**
 * Setter type for group shared state (accepts an updater function, same as Jotai's useAtom setter).
 */
export type SetGroupState = (updater: (prev: GroupSharedState) => GroupSharedState) => void;

/**
 * Jotai atom family for group shared state — used as fallback when no GroupStateContext is provided.
 * Each groupId gets its own isolated state atom.
 */
const groupSharedStateFamily = atomFamily((_groupId: GroupId) =>
  atom<GroupSharedState>({
    selectedSymbol: undefined,
    selectedTimeframe: "1h",
    selectedAccount: undefined,
  })
);

/**
 * Hook to access shared state for a widget group.
 * Returns a [state, setter] tuple — identical API whether backed by Redux (cloud-ui) or Jotai (Atlas).
 *
 * - If a `GroupStateContext` provider is mounted (e.g. `ReduxGroupStateProvider` in cloud-ui),
 *   reads/writes go through that provider.
 * - If no provider is mounted (Atlas), falls back to the per-group Jotai atom.
 *
 * Widget code never needs to know which backend is active.
 *
 * @example
 * // Read-only
 * const [sharedState] = useGroupSharedState(groupId);
 * const symbol = sharedState.selectedSymbol;
 *
 * @example
 * // Read + write
 * const [sharedState, setSharedState] = useGroupSharedState(groupId);
 * setSharedState((prev) => ({ ...prev, selectedPortfolioId: "abc-123" }));
 */
export function useGroupSharedState(groupId?: GroupId): [GroupSharedState, SetGroupState] {
  const effectiveGroupId = groupId ?? "default";
  const ctx = useContext(GroupStateContext);

  // Always call the Jotai hook regardless of ctx — React's rules of hooks require
  // all hooks to be called unconditionally on every render.
  const jotaiAtom = groupSharedStateFamily(effectiveGroupId);
  const [jotaiState, setJotaiState] = useAtom(jotaiAtom);

  if (ctx !== null) {
    // Redux (or any other) provider is mounted — use context-backed state.
    return [
      ctx.getGroupState(effectiveGroupId),
      (updater) => ctx.setGroupState(effectiveGroupId, updater),
    ];
  }

  // No provider — use Jotai atom directly (Atlas fallback).
  return [jotaiState, setJotaiState];
}
